import { useState } from 'react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NEWSLETTER_API = 'https://amityonline.com/api/newsletter';
const INVALID_EMAIL = 'Please enter a valid email address.';
const GENERIC_ERROR = 'Something went wrong! Please try again later.';

type SubscribeResult = 'subscribed' | 'already-subscribed' | 'failed';

function isValidEmail(value: string) {
  return !!value && !/\s/.test(value) && EMAIL_REGEX.test(value);
}

function fireWebEngage(email: string) {
  if (typeof window !== "undefined" && window.webengage) {
    window.webengage.track("Newsletter Subscribed", { email });
  }
}

async function subscribeToNewsletter(email: string): Promise<SubscribeResult> {
  const res = await fetch(`${NEWSLETTER_API}?email=${encodeURIComponent(email)}`);
  const resData = await res.json();

  if (resData?.data?.newsletters?.data?.length > 0) return 'already-subscribed';

  const postRes = await fetch(NEWSLETTER_API, {
    method: 'POST',
    body: JSON.stringify({ email }),
    headers: { 'Content-Type': 'application/json' },
  });
  const postData = await postRes.json();

  return postData?.success ? 'subscribed' : 'failed';
}

export function useNewsletterForm() {
  const [email, setEmail]           = useState('');
  const [emailError, setEmailError] = useState('');
  const [message, setMessage]       = useState('');
  const [loading, setLoading]       = useState(false);

  function validate() {
    const valid = isValidEmail(email);
    setEmailError(valid ? '' : INVALID_EMAIL);
    return valid;
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setEmail(val);
    setEmailError(!val || isValidEmail(val) ? '' : INVALID_EMAIL);
  }

  function handleResult(result: SubscribeResult) {
    if (result === 'already-subscribed') {
      setEmailError('You are already subscribed!');
      setTimeout(() => setEmailError(''), 4000);
    } else if (result === 'subscribed') {
      setMessage('Thank you for subscribing!');
      fireWebEngage(email);
      setEmail('');
    } else {
      setEmailError(GENERIC_ERROR);
    }
  }

  async function handleSubmit() {
    setMessage('');
    if (!validate()) return;

    setLoading(true);
    try {
      handleResult(await subscribeToNewsletter(email));
    } catch {
      setEmailError(GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  }

  return { email, emailError, message, loading, handleChange, handleBlur: validate, handleSubmit };
}
